import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { Project, ProjectStateTypes } from '../types/project'
import { useRouter } from 'next/router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ClientMethodsTypes, client } from '@/services/v2/client'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import { useMemo } from 'react'

const apiUrl = `/api/v1/users`

interface InvalidateQueryProps {
  projectId?: string
}

const useInvalidationQueries = () => {
  const queryClient = useQueryClient()
  const { profile } = useProfile()

  return async (data?: InvalidateQueryProps) => {
    const { projectId } = data || {}

    if (projectId) {
      await Promise.all([
        queryClient.invalidateQueries([ProfileQueryKeys.PROJECT, projectId]),
        queryClient.invalidateQueries([ProfileQueryKeys.PROJECTS, profile?.id]),
        queryClient.invalidateQueries([
          ProfileQueryKeys.HIGHLIGHTS,
          profile?.id,
        ]),
      ])

      return
    }

    await Promise.all([
      queryClient.invalidateQueries([ProfileQueryKeys.PROJECTS, profile?.id]),
      queryClient.invalidateQueries([ProfileQueryKeys.HIGHLIGHTS, profile?.id]),
    ])
  }
}

export const useProjects = () => {
  const { profile } = useProfile()
  const isOwnProfile = useIsOwnProfile()

  const { data, ...rest } = useQuery({
    queryFn: () =>
      client<Project[]>(`${apiUrl}/project/by-user/${profile?.id}`),
    queryKey: [ProfileQueryKeys.PROJECTS, profile?.id],
  })

  const projects = useMemo(() => {
    // Sort by created date in descending order
    const sortedProjects =
      data?.sort((a, b) => {
        // Given new projects use createOn as number and old projects is an object { _seconds: number, _nanoseconds: number }
        // We need to check if it's an object and convert it to number
        const aDate =
          typeof a.createdOn === 'object' ? a.createdOn._seconds : a.createdOn
        const bDate =
          typeof b.createdOn === 'object' ? b.createdOn._seconds : b.createdOn

        return bDate - aDate
      }) || []

    const convertedDateProjects = sortedProjects.map((project) => {
      return {
        ...project,
        createdOn: new Date(
          typeof project.createdOn === 'object'
            ? project.createdOn._seconds * 1000
            : project.createdOn
        ).getTime(),
        gallery: project?.gallery?.filter((gallery) => !!gallery) || [],
      }
    })

    return convertedDateProjects
  }, [data])

  const filteredProjects = useMemo(() => {
    if (isOwnProfile) return projects

    return (
      projects?.filter(
        (project) => project.state === ProjectStateTypes.PUBLISHED
      ) || []
    )
  }, [projects, isOwnProfile])

  return {
    data: filteredProjects,
    ...rest,
  }
}

export const useProject = (projectIdParam?: string) => {
  const { query } = useRouter()
  const projectId = projectIdParam || (query.project_id as string)

  const { data, ...rest } = useQuery({
    queryFn: () => client<Project>(`${apiUrl}/project/${projectId}`),
    queryKey: [ProfileQueryKeys.PROJECT, projectId],
    enabled: !!projectId,
  })

  const project = useMemo(() => {
    if (!data) return data

    return {
      ...data,
      createdOn: new Date(
        // some legacy projects have malformed createdOn values
        typeof data.createdOn === 'object'
          ? data.createdOn._seconds * 1000
          : data.createdOn
      ).getTime(),
    }
  }, [data])

  return {
    project,
    ...rest,
  }
}

export const useDeleteProject = () => {
  const onSettled = useInvalidationQueries()

  return useMutation({
    mutationFn: async (project: Project) =>
      await client(`${apiUrl}/project/delete/${project.id}`, {
        method: ClientMethodsTypes.DELETE,
        body: project,
      }),

    onSettled: (_, _error, variables) => onSettled({ projectId: variables.id }),
  })
}

export const useUpdateProject = () => {
  const onSettled = useInvalidationQueries()

  return useMutation({
    mutationFn: async (project: Project) =>
      await client<Project>(`${apiUrl}/project/update`, {
        method: ClientMethodsTypes.PATCH,
        body: project,
      }),

    onSettled: (_, _error, variables) => onSettled({ projectId: variables.id }),
  })
}

export const useCreateProject = () => {
  const onSettled = useInvalidationQueries()

  return useMutation({
    mutationFn: async (project: Project) =>
      await client<Project>(`${apiUrl}/project/create`, {
        method: ClientMethodsTypes.POST,
        body: project,
      }),

    onSettled: () => onSettled(),
  })
}
